import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MessageCircle, ArrowRight, ShieldCheck, BadgeCheck, MapPin, ArrowUpRight, Building2, Hotel, ShoppingCart, UtensilsCrossed, Fuel, Briefcase, Factory, Home as HomeIcon } from 'lucide-react';
import { FadeUp, MaskedLine, lineReveal } from '../components/Reveal';
import SectionHeading from '../components/SectionHeading';
import Marquee from '../components/Marquee';
import StatCounter from '../components/StatCounter';
import ServiceCard from '../components/ServiceCard';
import { site, stats, services, normsMarquee, testimonials, engineer, images, clientTypes, waUrl, DEFAULT_WA_MESSAGE } from '../data/site';

const CLIENT_ICONS = {
  building: Building2,
  hotel: Hotel,
  cart: ShoppingCart,
  utensils: UtensilsCrossed,
  fuel: Fuel,
  briefcase: Briefcase,
  factory: Factory,
  home: HomeIcon,
};

function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 90]);
  const yGlow = useTransform(scrollY, [0, 800], [0, -70]);

  return (
    <section className="relative overflow-hidden bg-blueprint lg:min-h-screen" data-testid="hero-section">
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-48 right-[-12%] h-[620px] w-[620px] rounded-full bg-azure/10 blur-[140px]" />
        <div className="absolute bottom-[-25%] left-[-12%] h-[560px] w-[560px] rounded-full bg-gold/15 blur-[150px]" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center gap-4 pt-32 text-center md:pt-36"
        >
          <p
            className="font-display text-3xl font-extrabold uppercase tracking-[0.18em] text-navy dark:text-white sm:text-4xl md:text-5xl lg:text-6xl"
            data-testid="hero-brand"
          >
            Grupo <span className="text-azure dark:text-gold-light">Majo</span>
          </p>
          <p className="font-display text-[11px] font-bold uppercase tracking-[0.4em] text-muted-c md:text-sm" data-testid="hero-brand-tagline">
            Engenharia • Processos • Intermediações
          </p>
          <div className="mt-3 flex items-center justify-center gap-5">
            <span className="hidden h-px w-16 bg-gradient-to-r from-transparent to-gold sm:block md:w-28" aria-hidden="true" />
            <p
              className="text-center font-display text-base font-extrabold uppercase leading-snug tracking-[0.28em] text-navy dark:text-gold-light sm:text-xl md:text-2xl lg:text-[1.65rem]"
              data-testid="hero-slogan"
            >
              O sonho é seu, mas o trabalho é nosso
            </p>
            <span className="hidden h-px w-16 bg-gradient-to-l from-transparent to-gold sm:block md:w-28" aria-hidden="true" />
          </div>
        </motion.div>
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-24 pt-12 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:pb-28 lg:pt-16">
        <div className="lg:col-span-7">
          <motion.h1
            variants={lineReveal}
            initial="hidden"
            animate="visible"
            className="mt-7 font-display text-[2.6rem] font-extrabold leading-[1.06] tracking-tight text-navy dark:text-white sm:text-6xl lg:text-[4.35rem]"
          >
            <MaskedLine>Projetos, laudos&nbsp;e</MaskedLine>
            <MaskedLine><span className="text-azure dark:text-gold-light">soluções técnicas</span></MaskedLine>
            <MaskedLine>para a sua obra.</MaskedLine>
          </motion.h1>

          <FadeUp delay={0.55}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-muted-c md:text-lg">
              {site.tagline}. Segurança jurídica, conformidade ABNT e tranquilidade técnica para construir, reformar e regularizar com ART registrada no CREA em todo o Brasil.
            </p>
          </FadeUp>

          <FadeUp delay={0.7} className="mt-9 flex flex-col gap-3.5 sm:flex-row">
            <a
              href={waUrl(DEFAULT_WA_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="btn-gold"
            >
              <MessageCircle size={17} /> Fale com o Engenheiro no WhatsApp
            </a>
            <Link to="/simulador" data-testid="hero-simulator-cta" className="btn-outline text-navy dark:text-white">
              Simular Orçamento <ArrowRight size={16} />
            </Link>
          </FadeUp>

          <FadeUp delay={0.85} className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs font-semibold uppercase tracking-wider text-muted-c">
            <span className="inline-flex items-center gap-2"><BadgeCheck size={15} className="text-gold" /> CREA {site.contact.crea}</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck size={15} className="text-gold" /> ART em 100% dos serviços</span>
            <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-gold" /> Atendimento nacional</span>
          </FadeUp>
        </div>

        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <FadeUp delay={0.4}>
            <div className="relative">
              <span className="absolute -left-3 -top-3 z-10 select-none font-display text-2xl text-gold" aria-hidden="true">+</span>
              <span className="absolute -bottom-3 -right-3 z-10 select-none font-display text-2xl text-gold" aria-hidden="true">+</span>
              <div className="overflow-hidden rounded-3xl border border-line shadow-[0_40px_90px_-30px_rgba(13,33,69,0.5)]">
                <img
                  src={images.hero}
                  alt="Eng. Amilcar em obra com plantas do projeto"
                  className="aspect-[4/5] w-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/55 via-transparent to-transparent" aria-hidden="true" />
              </div>

              <div className="absolute -bottom-8 -left-3 w-60 rounded-2xl border border-line bg-white/90 p-5 shadow-xl backdrop-blur-xl dark:bg-navy-surface/90 md:-left-10">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-c">Documentos e laudos a partir de</p>
                <p className="mt-1.5 font-display text-3xl font-extrabold text-navy dark:text-gold-light">R$ 650,00</p>
                <p className="mt-0.5 text-xs text-muted-c">+ taxas do conselho</p>
              </div>

              <div className="absolute -right-2 top-6 rounded-full border border-gold/40 bg-navy px-4 py-2 font-display text-[11px] font-bold uppercase tracking-widest text-gold-light shadow-lg dark:bg-navy-surface">
                ART no CREA · 100%
              </div>
            </div>
          </FadeUp>
        </motion.div>
      </div>
    </section>
  );
}

function StatsBand() {
  return (
    <section className="border-y border-line bg-[var(--surface)]" data-testid="stats-band">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
        {stats.map((s, i) => (
          <FadeUp key={s.label} delay={i * 0.08}>
            <StatCounter {...s} />
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

function ClientTypes() {
  return (
    <section className="mx-auto max-w-7xl px-5 pt-24 lg:px-8" data-testid="client-types-band">
      <FadeUp>
        <span className="eyebrow">Quem atendemos</span>
        <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-navy dark:text-white md:text-3xl">
          +200 obras e estabelecimentos atendidos: comércios, indústrias, condomínios e residências em todo o Brasil
        </h2>
      </FadeUp>
      <FadeUp delay={0.15}>
        <div className="mt-8 flex flex-wrap gap-3">
          {clientTypes.map((c, i) => {
            const Icon = CLIENT_ICONS[c.icon];
            return (
              <span
                key={c.label}
                data-testid={`client-type-${i}`}
                className="inline-flex items-center gap-2.5 rounded-full border border-line bg-[var(--surface)] px-5 py-3 font-display text-sm font-semibold text-navy transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-lg dark:text-white"
              >
                <Icon size={17} className="text-gold" /> {c.label}
              </span>
            );
          })}
        </div>
      </FadeUp>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="services-preview">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHeading
          eyebrow="Áreas de Atuação"
          title="Engenharia completa, da fundação ao laudo final"
          description="Oito frentes técnicas sob a mesma responsabilidade: projetos, ART, laudos, inspeção predial, instalações elétricas, segurança do trabalho, regularização e arquitetura."
        />
        <FadeUp delay={0.2}>
          <Link to="/servicos" data-testid="services-preview-cta" className="btn-outline shrink-0 text-navy dark:text-white">
            Ver todas as áreas <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </div>
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {services.slice(0, 3).map((s, i) => (
          <FadeUp key={s.id} delay={i * 0.08} className="lg:first:col-span-2">
            <ServiceCard service={s} />
          </FadeUp>
        ))}
        {services.slice(3, 8).map((s, i) => (
          <FadeUp key={s.id} delay={(i + 3) * 0.06}>
            <ServiceCard service={s} />
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

function EngineerPreview() {
  return (
    <section className="border-y border-line bg-[var(--surface)]" data-testid="engineer-preview">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 lg:grid-cols-2 lg:px-8 lg:py-28">
        <FadeUp>
          <div className="relative max-w-md">
            <span className="absolute -left-3 -top-3 select-none font-display text-2xl text-gold" aria-hidden="true">+</span>
            <span className="absolute -bottom-3 -right-3 select-none font-display text-2xl text-gold" aria-hidden="true">+</span>
            <img
              src={engineer.photo}
              alt={engineer.name}
              className="aspect-[4/3] w-full rounded-3xl object-cover object-center shadow-[0_40px_90px_-35px_rgba(13,33,69,0.55)]"
              data-testid="engineer-preview-photo"
            />
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-gold/30 bg-navy/90 p-4 text-white backdrop-blur-md">
              <p className="font-display text-sm font-bold">{engineer.name}</p>
              <p className="text-xs text-gold-light">{engineer.crea}</p>
            </div>
          </div>
        </FadeUp>
        <div>
          <SectionHeading
            eyebrow="Responsável Técnico"
            title={engineer.name}
            description={engineer.bio}
          />
          <FadeUp delay={0.2} className="mt-8 flex flex-wrap gap-2.5">
            {engineer.certifications.map((c) => (
              <span key={c} className="chip">{c}</span>
            ))}
          </FadeUp>
          <FadeUp delay={0.3} className="mt-9 flex flex-col gap-3.5 sm:flex-row">
            <Link to="/responsavel-tecnico" data-testid="engineer-preview-cta" className="btn-gold">
              Conhecer o Engenheiro <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function PricingBand() {
  return (
    <section className="relative overflow-hidden bg-navy py-24 text-white lg:py-28" data-testid="pricing-band">
      <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2 lg:px-8">
        <SectionHeading
          light
          eyebrow="Preço Âncora"
          title="Documentos e laudos a partir de R$ 650,00"
          description="Emissão de documentos técnicos com responsabilidade do Eng. Amilcar Sottani Faccion, com ART registrada no CREA. Valores adicionais referentes às taxas do conselho."
        />
        <FadeUp delay={0.2} className="lg:justify-self-end">
          <div className="rounded-3xl border border-gold/30 bg-white/5 p-10 backdrop-blur-md">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-gold-light">Investimento inicial</p>
            <p className="mt-3 font-display text-6xl font-extrabold text-gold-light md:text-7xl">R$ 650,00</p>
            <p className="mt-2 text-sm text-white/60">+ taxas do conselho · sob consulta de escopo</p>
            <Link to="/simulador" data-testid="pricing-band-cta" className="btn-gold mt-8 w-full">
              Simular meu orçamento <ArrowRight size={16} />
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function TestimonialPreview() {
  const t = testimonials[0];
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="testimonial-preview">
      <SectionHeading
        eyebrow="Depoimentos"
        title="Quem contratou, recomenda"
      />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {testimonials.slice(0, 3).map((t, i) => (
          <FadeUp key={t.client} delay={i * 0.08}>
            <article className="card-hover flex h-full flex-col rounded-2xl surface p-7" data-testid={`testimonial-card-${i}`}>
              <div className="flex gap-1 text-gold" aria-label={`${t.rating} estrelas`}>
                {Array.from({ length: t.rating }).map((_, s) => (
                  <svg key={s} width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.9 6.26 6.6.7-4.9 4.5 1.35 6.54L12 16.9 6.05 20l1.35-6.54-4.9-4.5 6.6-.7L12 2z"/></svg>
                ))}
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-c">“{t.text}”</p>
              <div className="mt-6 border-t border-line pt-4">
                <p className="font-display text-sm font-bold text-navy dark:text-white">{t.client}</p>
                <p className="mt-0.5 text-xs text-muted-c">{t.role}</p>
              </div>
            </article>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2} className="mt-10 text-center">
        <Link to="/depoimentos" data-testid="testimonial-preview-cta" className="btn-outline text-navy dark:text-white">
          Ver todos os depoimentos <ArrowRight size={16} />
        </Link>
      </FadeUp>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden border-t border-line bg-blueprint py-24 lg:py-32" data-testid="final-cta">
      <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
        <FadeUp>
          <p className="font-display text-3xl font-extrabold leading-tight tracking-tight text-navy dark:text-white md:text-5xl">
            O sonho é <span className="text-azure dark:text-gold-light">seu</span>,<br />mas o trabalho é <span className="text-gold">nosso</span>.
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="mx-auto mt-6 max-w-xl text-muted-c">
            Envie sua demanda agora e receba um retorno técnico direto com o engenheiro responsável — sem robôs, sem enrolação.
          </p>
        </FadeUp>
        <FadeUp delay={0.3} className="mt-9 flex flex-col justify-center gap-3.5 sm:flex-row">
          <a href={waUrl(DEFAULT_WA_MESSAGE)} target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp" className="btn-gold">
            <MessageCircle size={17} /> Chamar no WhatsApp
          </a>
          <Link to="/contato" data-testid="final-cta-contact" className="btn-outline text-navy dark:text-white">
            Fale Conosco
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <StatsBand />
      <Marquee items={normsMarquee} />
      <ClientTypes />
      <ServicesPreview />
      <EngineerPreview />
      <PricingBand />
      <TestimonialPreview />
      <FinalCta />
    </>
  );
}
